import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { ApiError } from "@/core/api/apiTypes";
import { conversationKeys } from "@/features/messages/hooks/conversationQueries";
import { searchKeys } from "@/features/search/hooks/searchQueries";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { clientApiService } from "../services/clientApiService";
import {
  fromClientDtoToClient,
  fromCreateClientRequestToCreateClientDto,
  fromUpdateClientRequestToUpdateClientDto,
  type Client,
  type CreateClientRequest,
  type UpdateClientRequest,
} from "../types/clientTypes";

const staleTime = 5 * 60 * 1000;

export const clientKeys = {
  all: ["client"] as const,
  list: (userId?: string, businessId?: string) => [...clientKeys.all, "list", { userId, businessId }] as const,
  detail: (id: string, userId?: string, businessId?: string) => [...clientKeys.all, "detail", id, { userId, businessId }] as const,
};

interface ClientMutationScope {
  userId: string | undefined;
  businessId: string | undefined;
}

async function updateClientCache(queryClient: QueryClient, client: Client, scope: ClientMutationScope | undefined) {
  if (!scope || client.businessId !== scope.businessId) return;
  const listKey = clientKeys.list(scope.userId, scope.businessId);
  const detailKey = clientKeys.detail(client.id, scope.userId, scope.businessId);
  await Promise.all([
    queryClient.cancelQueries({ queryKey: listKey }),
    queryClient.cancelQueries({ queryKey: detailKey }),
  ]);
  // Auth changes remove queries; a late save must not recreate the previous account's cache.
  if (queryClient.getQueryState(listKey) || queryClient.getQueryState(detailKey)) {
    queryClient.setQueryData(detailKey, client);
    queryClient.setQueryData<Client[]>(listKey, (clients) =>
      clients ? [...clients.filter((item) => item.id !== client.id), client] : undefined);
    void queryClient.invalidateQueries({ queryKey: listKey });
  }
  void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  void queryClient.invalidateQueries({ queryKey: searchKeys.all });
}

export const useClients = () => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<Client[], Error>({
    queryKey: clientKeys.list(user?.id, businessId),
    queryFn: async ({ signal }) => {
      if (!user || !businessId) throw new Error("Select a business to load customers.");
      const dtos = await clientApiService.getAll(businessId, signal);
      return dtos.map(fromClientDtoToClient);
    },
    enabled: !!user && !!businessId,
    staleTime,
  });
};

export const useClient = (id: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<Client, Error>({
    queryKey: clientKeys.detail(id, user?.id, businessId),
    queryFn: async ({ signal }) => {
      const dto = await clientApiService.getById(id, signal);
      const client = fromClientDtoToClient(dto);
      if (client.businessId !== businessId) throw new ApiError("Customer not found in this business.", 404);
      return client;
    },
    enabled: !!id && !!user && !!businessId,
    retry: (failureCount, error) => !(error instanceof ApiError && (error.Status === 404 || error.Status === 403)) && failureCount < 3,
    staleTime,
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useMutation<Client, Error, CreateClientRequest, ClientMutationScope>({
    meta: { action: "client.create" },
    onMutate: () => ({ userId: user?.id, businessId }),
    mutationFn: async (data: CreateClientRequest) => {
      if (!user || !businessId || data.businessId !== businessId) throw new Error("Select a business before adding a customer.");
      const dto = fromCreateClientRequestToCreateClientDto(data);
      const resultDto = await clientApiService.create(dto);
      return fromClientDtoToClient(resultDto);
    },
    onSuccess: (client, _data, scope) => updateClientCache(queryClient, client, scope),
  });
};

export const useUpdateClient = (id: string) => {
  const queryClient = useQueryClient();
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useMutation<Client, Error, Partial<UpdateClientRequest>, ClientMutationScope>({
    meta: { action: "client.update" },
    onMutate: () => ({ userId: user?.id, businessId }),
    mutationFn: async (data: Partial<UpdateClientRequest>) => {
      if (!id || !user || !businessId) throw new Error("Select a customer before saving.");
      const dto = fromUpdateClientRequestToUpdateClientDto(
        data as UpdateClientRequest
      );
      const resultDto = await clientApiService.update(id, dto);
      return fromClientDtoToClient(resultDto);
    },
    onSuccess: (client, _data, scope) => updateClientCache(queryClient, client, scope),
  });
};

export const useDeleteClient = (id: string) => {
  const queryClient = useQueryClient();
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useMutation<{ message: string }, Error, void, ClientMutationScope>({
    meta: { action: "client.delete" },
    onMutate: () => ({ userId: user?.id, businessId }),
    mutationFn: () => clientApiService.delete(id),
    onSuccess: (_result, _data, scope) => {
      if (!scope) return;
      queryClient.removeQueries({ queryKey: clientKeys.detail(id, scope.userId, scope.businessId) });
      void queryClient.invalidateQueries({ queryKey: clientKeys.list(scope.userId, scope.businessId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      void queryClient.invalidateQueries({ queryKey: searchKeys.all });
    },
  });
};
